import { useState, FormEvent } from "react";
import { supabase } from "../lib/supabase";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export function SuggestPodcastForm() {
  const { user } = useAuth();
  const location = useLocation();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [nameError, setNameError] = useState("");
  const [urlError, setUrlError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  
  // Return URL for login redirect
  const returnUrl = encodeURIComponent(location.pathname);

  const validateForm = () => {
    let isValid = true;
    
    // Validate name
    if (!name.trim()) {
      setNameError("Podcast name is required");
      isValid = false;
    } else {
      setNameError("");
    }
    
    // Validate URL if provided
    if (url.trim() && !url.match(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/)) {
      setUrlError("Please enter a valid URL");
      isValid = false;
    } else {
      setUrlError("");
    }
    
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSuccess(false);
    setSubmitError("");
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Check auth status from context first
      if (!user) {
        throw new Error("You must be logged in to suggest a podcast");
      }
      
      // Insert podcast suggestion
      const { error } = await supabase.from("podcast_suggestions").insert({
        user_id: user.id,
        name: name.trim(),
        url: url.trim() || null,
      });
      
      if (error) throw error;
      
      // Show success state
      setIsSuccess(true);
      
      // Reset form
      setName("");
      setUrl("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to submit podcast suggestion";
      console.error("Error submitting podcast suggestion:", error);
      setSubmitError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="bg-[#DCD9E8] p-6">
        <h2 className="text-2xl font-semibold">Suggest a Podcast</h2>
        <p className="text-gray-600 mt-1">
          Know a great podcast that should be featured? Let us know!
        </p>
      </div>
      
      <div className="p-6">
        {isSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            <p className="font-medium">Thank you!</p>
            <p>Your podcast suggestion has been submitted.</p>
          </div>
        )}
        
        {submitError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {submitError.includes("logged in") ? (
              <div>
                <p className="font-medium">Please log in</p>
                <div className="mt-3">
                  <Link 
                    to={`/login?returnTo=${returnUrl}`}
                    className="inline-block bg-black text-white py-2 px-4 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Login to continue
                  </Link>
                </div>
              </div>
            ) : (
              <div>
                <p className="font-medium">Something went wrong</p>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="podcast-name">
              Podcast Name
            </label>
            <input
              type="text"
              id="podcast-name"
              className={`w-full px-3 py-2 border rounded-md ${
                nameError ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter podcast name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
            {nameError && (
              <p className="mt-1 text-sm text-red-600">{nameError}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="podcast-url">
              Podcast URL (optional)
            </label>
            <input
              type="text"
              id="podcast-url"
              className={`w-full px-3 py-2 border rounded-md ${
                urlError ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="https://example.com/podcast"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isLoading}
            />
            {urlError && (
              <p className="mt-1 text-sm text-red-600">{urlError}</p>
            )}
          </div>
          
          <button
            type="submit"
            className="w-full bg-black text-white py-2 px-4 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Submit Suggestion"}
          </button>
        </form>
      </div>
    </div>
  );
} 
